import React, { useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { gql, useQuery } from '@apollo/client';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';

import { RsvpStatCard } from '../../Attendance/rsvp-stat-card.component';
import { RsvpBadge } from '../../Attendance/rsvp-badge.component';
import { validateAttendanceRatio, hasSessionOccurred, getRsvpReasonText } from '../../Attendance/attendance.helpers';
import { numToTime } from '../../../lib/helpers/conversion.helpers';

interface IPlayerAttendanceProps {
  playerAssessmentId: string;
}

const GET_PLAYER_ATTENDANCE = gql`
  query GetPlayerAttendance($playerAssessmentId: ID!) {
    playerAssessment(_id: $playerAssessmentId) {
      _id
      attendanceCount
      assessmentSessionCount
      rsvpAttendingCount
      rsvpRequestedCount
    }
    playerAssessmentSessions(playerAssessmentId: $playerAssessmentId) {
      _id
      date
      start
      isAttendanceRequested
      checkedInPlayerIds
      attendanceResponses {
        _id
        playerAssessmentId
        status
        reason
        reasonNote
      }
    }
  }
`;

const RECENT_SESSION_LIMIT = 5;

export const PlayerAttendanceGraphQL: React.FC<IPlayerAttendanceProps> = ({ playerAssessmentId }) => {
  const { data, loading } = useQuery(GET_PLAYER_ATTENDANCE, {
    variables: { playerAssessmentId },
    fetchPolicy: 'cache-and-network',
  });

  const player = data?.playerAssessment as IPlayerAssessment | undefined;
  const sessions = (data?.playerAssessmentSessions as IAssessmentSession[]) || [];

  const recentSessions = useMemo(
    () => _.orderBy(sessions, ['date', 'start'], ['desc', 'desc']).slice(0, RECENT_SESSION_LIMIT),
    [sessions],
  );
  const attendedRatio = useMemo(
    () => validateAttendanceRatio(
      player?.attendanceCount,
      player?.assessmentSessionCount,
      `PlayerAttendance.attended[${playerAssessmentId}]`,
    ),
    [player?.attendanceCount, player?.assessmentSessionCount, playerAssessmentId],
  );
  const rsvpRatio = useMemo(
    () => validateAttendanceRatio(
      player?.rsvpAttendingCount,
      player?.rsvpRequestedCount,
      `PlayerAttendance.rsvp[${playerAssessmentId}]`,
    ),
    [player?.rsvpAttendingCount, player?.rsvpRequestedCount, playerAssessmentId],
  );

  if (loading && !data) return <CircularProgress />;
  if (!player) return null;

  return (
    <Box>
      <Box display="flex" style={{ gap: '1rem', flexWrap: 'wrap' }} mb={3}>
        <RsvpStatCard
          label="Attended"
          numerator={attendedRatio?.attended}
          denominator={attendedRatio?.total}
        />
        <RsvpStatCard
          label="RSVP'd Yes"
          numerator={rsvpRatio?.attended}
          denominator={rsvpRatio?.total}
        />
      </Box>

      <Typography variant="subtitle2" gutterBottom>Recent ice times</Typography>
      <Divider />
      {recentSessions.length === 0 && (
        <Box py={2}>
          <Typography variant="body2" color="textSecondary">No recent ice times.</Typography>
        </Box>
      )}
      {recentSessions.length > 0 && (
        <Box display="flex" alignItems="center" py={1} style={{ borderBottom: '1px solid #eee' }}>
          <Box flex="0 0 200px"><Typography variant="caption" color="textSecondary">Date</Typography></Box>
          <Box flex="0 0 140px"><Typography variant="caption" color="textSecondary">Checked in</Typography></Box>
          <Box flex="0 0 auto"><Typography variant="caption" color="textSecondary">RSVP</Typography></Box>
        </Box>
      )}
      {recentSessions.map((session) => {
        // `checkedInPlayerIds` is misleadingly named — its contents are
        // `PlayerAssessment._id`s, not `Player._id`s. The backend stores
        // PlayerAssessment references under this field. We deliberately
        // compare against `playerAssessmentId` here, not against `player._id`.
        const wasAttended = session.checkedInPlayerIds.map(String).includes(String(playerAssessmentId));
        const rsvp = (session.attendanceResponses || []).find((r) => String(r.playerAssessmentId) === String(playerAssessmentId));
        const rsvpDisplay: AttendanceStatus = rsvp ? rsvp.status : 'pending';
        const sessionRequested = Boolean(session.isAttendanceRequested);
        // `playerAssessmentSessions` returns future ice times too. One that
        // hasn't happened yet can't have been attended or missed — show a
        // neutral '-' rather than implying the player failed to show up.
        const sessionOccurred = hasSessionOccurred(session);

        return (
          <Box key={session._id} display="flex" alignItems="center" py={1.5} style={{ borderBottom: '1px solid #eee' }}>
            <Box flex="0 0 200px">
              <Typography variant="body2">
                {dayjs(session.date).format('MMM D')} &middot; {numToTime(session.start)}
              </Typography>
            </Box>
            <Box flex="0 0 140px">
              {sessionOccurred ? (
                <Typography variant="body2" style={{ color: wasAttended ? '#10dc60' : '#f04141' }}>
                  {wasAttended ? '✓ Attended' : '✗ Missed'}
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">-</Typography>
              )}
            </Box>
            <Box flex="0 0 auto">
              {sessionRequested ? (
                <>
                  <RsvpBadge status={rsvpDisplay} />
                  {getRsvpReasonText(rsvp) && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {getRsvpReasonText(rsvp)}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">RSVP not requested</Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

